import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Select, Popup, Button, BUTTON_TYPES, User } from '_atoms';
import { fetchWrapper } from 'helpers/helpers';
import { getUser } from 'store/selectors/authorizeSelectors';
import { ROLES } from 'permission';
import { BUTTON_TEXT } from 'constants/text';
import './styles.scss';

import type { Uuid } from 'store/reducers/types';
import type { SelectOption } from '_atoms/Select';

interface CurrentRecruiter {
  uuid: Uuid;
  full_name: string;
  avatar?: string | null;
}

interface RecruiterOption extends SelectOption {
  value: string;
  label: string;
}

interface PopupReassignResponsibleRecruiterProps {
  open: boolean;
  loading?: boolean;
  onOk: (recruiterUuid: string) => void;
  onClose: () => void;
  currentRecruiter: CurrentRecruiter | null | undefined;
}

const getRecruiters = (body: Record<string, unknown>) =>
  fetchWrapper({
    url: 'users/',
    body,
  }).catch(() => []);

export const PopupReassignResponsibleRecruiter: React.FC<PopupReassignResponsibleRecruiterProps> = ({
  open,
  loading,
  onOk,
  onClose,
  currentRecruiter,
}) => {
  const { t } = useTranslation();
  const user = useSelector(getUser);
  const [recruiter, setRecruiter] = useState<RecruiterOption | null>(null);
  const [options, setOptions] = useState<RecruiterOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const onAssignToMe = () => {
    setRecruiter(options.find(r => r.value === user.uuid) ?? null);
  };

  const showAssignToMe = useMemo(
    () => options.find(r => r.value === user.uuid) && recruiter?.value !== user.uuid,
    [options, recruiter]
  );

  useEffect(() => {
    if (!open) {
      setRecruiter(null);
      setOptions([]);
    } else {
      setOptionsLoading(true);
      (user.active_role?.name === ROLES.RECRUITER_LEAD
        ? Promise.all([
            getRecruiters({
              exclude_uuid__in: currentRecruiter?.uuid,
              role: [ROLES.RECRUITER],
              my_recruiters_group: true,
            }),
            getRecruiters({
              exclude_uuid__in: currentRecruiter?.uuid,
              role: [ROLES.RECRUITER_LEAD],
            }),
          ]).then(resp => resp.flat())
        : getRecruiters({
            exclude_uuid__in: currentRecruiter?.uuid,
            role: [ROLES.RECRUITER, ROLES.RECRUITER_LEAD],
          })
      )
        .then((users: { uuid: string; full_name: string }[]) =>
          setOptions(Array.from(new Map(users.map(u => [u.uuid, { value: u.uuid, label: u.full_name }])).values()))
        )
        .finally(() => setOptionsLoading(false));
    }
  }, [open, currentRecruiter]);
  return (
    <Popup
      className="popup-reassign-recruiter-recruiter"
      open={open}
      loading={loading}
      title={t('translation:popup_reassign_responsible_recruiter.title')}
      cancelButtonProps={{ onClick: onClose, children: BUTTON_TEXT.CANCEL }}
      okButtonProps={{
        disabled: !recruiter,
        onClick: () => recruiter && onOk(recruiter.value),
        children: BUTTON_TEXT.REASSIGN,
      }}
    >
      <User
        className="popup-reassign-recruiter__user"
        fullName={currentRecruiter?.full_name}
        avatar={currentRecruiter?.avatar}
      />
      <Select
        className="popup-reassign-recruiter__select"
        label={t('translation:popup_reassign_responsible_recruiter.label_reassign_to')}
        placeholder="Select responsible Recruiter"
        value={recruiter}
        onChange={setRecruiter}
        options={options}
        loading={optionsLoading}
        filterOption={(v: string, o: RecruiterOption) => o.label.toLowerCase().includes(v.toLowerCase())}
      />
      {showAssignToMe && (
        <Button className="popup-reassign-recruiter__assign-to-me" type={BUTTON_TYPES.TEXT} onClick={onAssignToMe}>
          {t('translation:popup_reassign_responsible_recruiter.button_assign_to_me')}
        </Button>
      )}
    </Popup>
  );
};
